/*
 * ТЕОРИЯ-МУЛЬТИК — большая модалка, которая открывается при входе в урок.
 *
 * ЗАЧЕМ ОНА. Раньше теория была текстом в узкой боковой панели, и это не
 * работало: дети не читают. Проверено на первой версии школы — писали хорошие
 * подробные уроки, их не читал никто.
 *
 * ФОРМАТ (общее решение по обучающим мультикам, 10.08.2026 —
 * БЭКАПЫ/HANDOFF_2026_08_10_NOVYY_FORMAT_MULTIKOV.md):
 * кадр показывает СХЕМУ РЕАЛЬНОГО ИНТЕРФЕЙСА редактора, по которой ездит
 * курсор и нажимаются кнопки. Кадры-действия анимируются, кадры-объяснения
 * статичны — анимация помогает процедурам (d ≈ 1.06) и почти не помогает
 * концепциям (d ≈ 0.37), зато грузит память транзиторностью.
 *
 * ЧТО ЗДЕСЬ, А ЧТО В ДВИЖКЕ. Здесь только оболочка: затемнение, шапка
 * с названием урока и кнопка «Пропустить». Само проигрывание — AnimMovie
 * из movie/: он держит кадр столько, сколько говорит диктор, гонит курсор
 * в нужном темпе и даёт перемотку по кадрам.
 *
 * ЗАПАСНОЙ ПУТЬ. Если у урока сценария ещё нет (написаны 1–3), показываем
 * прежний вид: анимация детали + титр. Так уроки без сценария остаются
 * рабочими, пока их не перевели на новый формат.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import CadDemo from './CadDemos';
import AnimMovie from './movie/AnimMovie';
import SCENARIOS from './movie/scenarios';
import { speakAsync, stopSpeaking, preloadVoices } from './speech';
import cl from './TheaterModal.module.css';


/** Пауза между кадрами запасного режима, мс. */
const FRAME_GAP = 450;

/** Сколько держать кадр без голоса (немой режим), мс. */
const SILENT_FRAME_MS = 5200;

/** Скорости диктора — те же, что в мультиках школы. */
const RATES = [0.75, 1, 1.25, 1.5];


/** Кадры запасного режима: абзац теории + привязанная анимация. */
function buildFrames(lesson) {
    const paragraphs = lesson.theory || [];
    const demos = lesson.demos || [];
    return paragraphs.map((text, i) => ({ text, demo: demos[i] || null }));
}


const TheaterModal = ({ lesson, onFinish }) => {
    /* Сценарий нового формата, если он написан для этого урока. */
    const scenario = SCENARIOS[lesson.id] || null;

    return (
        <div className={cl.backdrop} role="dialog" aria-modal="true"
            aria-label={`Теория урока: ${lesson.title}`}>
            <div className={cl.modal}>
                <header className={cl.head}>
                    <div className={cl.titles}>
                        <span className={cl.eyebrow}>Теория</span>
                        <h2 className={cl.title}>{lesson.title}</h2>
                    </div>
                    <button type="button" className={cl.skipTop} onClick={onFinish}>
                        Пропустить
                    </button>
                </header>

                {scenario
                    ? <AnimMovie scene={scenario} onFinish={onFinish}/>
                    : <LegacyPlayer lesson={lesson} onFinish={onFinish}/>}
            </div>
        </div>
    );
};


/*
 * ── ЗАПАСНОЙ ПРОИГРЫВАТЕЛЬ ────────────────────────────────────────────
 *
 * Прежний формат: анимация детали крупно + титр под ней, кадры идут в такт
 * голосу. Остаётся для уроков, которым сценарий ещё не написан.
 */
function LegacyPlayer({ lesson, onFinish }) {
    const [frames] = useState(() => buildFrames(lesson));
    const [index, setIndex] = useState(0);
    const [paused, setPaused] = useState(false);
    const [muted, setMuted] = useState(false);
    const [rate, setRate] = useState(1);

    const rateRef = useRef(1);
    const speechRef = useRef(null);
    const timerRef = useRef(null);
    const closedRef = useRef(false);

    const finish = useCallback(() => {
        if (closedRef.current) return;
        closedRef.current = true;
        stopSpeaking();
        clearTimeout(timerRef.current);
        onFinish();
    }, [onFinish]);

    useEffect(() => {
        rateRef.current = rate;
        speechRef.current?.setRate?.(rate);
    }, [rate]);

    useEffect(() => {
        preloadVoices(frames.map((f) => f.text)).catch(() => {});
    }, [frames]);

    useEffect(() => {
        if (closedRef.current) return undefined;
        const frame = frames[index];
        if (!frame) { finish(); return undefined; }

        let cancelled = false;
        const next = () => {
            if (cancelled || closedRef.current) return;
            if (index + 1 < frames.length) setIndex(index + 1);
            else finish();
        };

        if (muted) {
            timerRef.current = setTimeout(next, SILENT_FRAME_MS / rateRef.current);
        } else {
            const handle = speakAsync(frame.text, rateRef.current);
            speechRef.current = handle;
            handle.promise.then(() => {
                if (cancelled || closedRef.current) return;
                timerRef.current = setTimeout(next, FRAME_GAP);
            });
        }

        return () => {
            cancelled = true;
            clearTimeout(timerRef.current);
            stopSpeaking();
        };
    }, [index, frames, muted, finish]);

    const togglePause = useCallback(() => {
        setPaused((wasPaused) => {
            const handle = speechRef.current;
            if (wasPaused) handle?.resume();
            else handle?.pause();
            return !wasPaused;
        });
    }, []);

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === 'Escape') { e.preventDefault(); finish(); }
            else if (e.key === ' ') { e.preventDefault(); togglePause(); }
            else if (e.key === 'ArrowRight') {
                e.preventDefault();
                setIndex((i) => (i + 1 < frames.length ? i + 1 : i));
            } else if (e.key === 'ArrowLeft') {
                e.preventDefault();
                setIndex((i) => (i > 0 ? i - 1 : i));
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [finish, togglePause, frames.length]);

    useEffect(() => () => {
        closedRef.current = true;
        stopSpeaking();
    }, []);

    const frame = frames[index];
    if (!frame) return null;
    const isLast = index === frames.length - 1;

    return (
        <>
            <div className={cl.stage}>
                {frame.demo
                    ? <CadDemo key={index} {...frame.demo} bare/>
                    : <div className={cl.plainMark} aria-hidden="true">{index + 1}</div>}
            </div>

            <div className={cl.caption}>
                <p className={cl.capText}>{frame.text}</p>
            </div>

            <footer className={cl.foot}>
                <div className={cl.controls}>
                    <button type="button" className={cl.ctrlBtn}
                        onClick={() => setIndex((i) => (i > 0 ? i - 1 : i))}
                        disabled={index === 0} title="Предыдущий кадр (←)">Назад</button>
                    <button type="button" className={cl.ctrlBtn}
                        onClick={togglePause} title="Пауза (пробел)">
                        {paused ? 'Продолжить' : 'Пауза'}
                    </button>
                    <button type="button" className={cl.ctrlBtn}
                        onClick={() => { stopSpeaking(); setMuted((m) => !m); }}
                        title="Выключить или включить голос">
                        {muted ? 'Включить голос' : 'Без голоса'}
                    </button>
                    <div className={cl.rates} role="group" aria-label="Скорость">
                        {RATES.map((r) => (
                            <button key={r} type="button"
                                className={r === rate ? cl.rateOn : cl.rate}
                                onClick={() => setRate(r)} title={`Скорость ${r}×`}>
                                {r}×
                            </button>
                        ))}
                    </div>
                </div>

                <div className={cl.mainActions}>
                    <button type="button" className={cl.skipBtn} onClick={finish}>
                        Пропустить теорию
                    </button>
                    <button type="button" className={cl.nextBtn}
                        onClick={() => { if (isLast) finish(); else setIndex(index + 1); }}>
                        {isLast ? 'Перейти к заданию' : 'Дальше'}
                    </button>
                </div>
            </footer>
        </>
    );
}

export default TheaterModal;
